import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize } from 'rxjs';
import { NotificationData } from '../../../services/notification.service';
import { ModalStateService } from '../../../services/modal-state.service';
import { LoggerService } from '../../../services/logger.service';
import { splitLicensePlateForApi } from '../../../shared/utils/split-license-plate-for-api';
import { CUSTOMER_PHONE_MAX_DIGITS } from '../../../shared/validators/customer-contact-field.rules';
import { WayPurchaseApiService } from '../../way-purchase-modal/services/way-purchase-api.service';
import { WayPurchaseModalStateService } from '../../way-purchase-modal/services/way-purchase-modal-state.service';
import { openWayPurchaseModal } from '../../way-purchase-modal/utils/way-purchase-modal-open.util';
import {
  buildNotificationFromWayLookupResponse,
  extractWayPurchaseSignature,
} from '../../way-purchase-modal/utils/way-purchase-lookup-orchestration.util';

export type OrderRequestWayLookupKind = 'plate' | 'phone' | 'email';

export interface OrderRequestWayLookupParams {
  locationId: string | null;
  /** Backend-advertised capability; false/undefined skips the HTTP call. */
  wayPurchaseLookupEnabled?: boolean;
  plate?: string;
  phone?: string;
  email?: string;
  isStale: () => boolean;
  parentContext?: Record<string, any> | null;
}

@Injectable({ providedIn: 'root' })
export class NotificationOrderRequestWayLookupService {
  private inFlightKey: string | null = null;
  private latestRequestKey: string | null = null;
  private storedPurchaseNotification: NotificationData | null = null;
  private storedSearchKey: string | null = null;
  /** Locations that returned MISSING_WAY_VENDOR_LOCATION_ID this session. */
  private readonly missingWayVendorLocationIds = new Set<string>();

  /** True when a Way purchase was found for the current search and the banner may be shown. */
  readonly showWayPurchaseReopenBanner$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly wayPurchaseApi: WayPurchaseApiService,
    private readonly wayPurchaseModalState: WayPurchaseModalStateService,
    private readonly modalStateService: ModalStateService,
    private readonly logger: LoggerService
  ) {}

  lookupFromOrderRequest(params: OrderRequestWayLookupParams): void {
    const locationId = String(params.locationId ?? '').trim();
    if (!locationId) {
      return;
    }

    if (params.wayPurchaseLookupEnabled !== true) {
      return;
    }

    if (this.missingWayVendorLocationIds.has(locationId)) {
      return;
    }

    const kind = this.resolveKind(params);
    if (!kind) {
      return;
    }

    const value = this.resolveLookupValue(kind, params);
    if (!this.meetsMinimumInput(kind, value)) {
      return;
    }

    const body =
      kind === 'plate'
        ? this.buildPlateLookupBody(locationId, value, params.parentContext)
        : kind === 'phone'
          ? { location_id: locationId, phone: value }
          : { location_id: locationId, email: value };

    const keyValue =
      kind === 'plate' && 'plate_number' in body
        ? String(body.plate_number).toUpperCase()
        : value.toUpperCase();
    const requestKey = `${locationId}:${kind}:${keyValue}`;
    if (this.inFlightKey === requestKey) {
      return;
    }
    if (this.storedSearchKey && this.storedSearchKey !== requestKey) {
      this.clearStoredWayPurchase();
    }
    this.inFlightKey = requestKey;
    this.latestRequestKey = requestKey;

    this.wayPurchaseApi
      .lookupWayPurchases(body)
      .pipe(
        finalize(() => {
          if (this.inFlightKey === requestKey) {
            this.inFlightKey = null;
          }
        })
      )
      .subscribe({
        next: response => {
          if (params.isStale() || this.latestRequestKey !== requestKey) {
            return;
          }
          const purchases = response.data?.purchases ?? [];
          const notification = buildNotificationFromWayLookupResponse(purchases, {
            locationId,
            plate: kind === 'plate' ? value : undefined,
          });
          if (notification) {
            const merged = this.mergeParentContext(notification, params.parentContext);
            this.rememberPurchaseNotification(merged, requestKey);
            this.openWayModalIfNew(merged);
          } else if (this.storedSearchKey === requestKey) {
            this.clearStoredWayPurchase();
          }
        },
        error: error => this.handleLookupError(error, locationId, params.isStale, requestKey),
      });
  }

  private buildPlateLookupBody(
    locationId: string,
    plate: string,
    parentContext?: Record<string, any> | null
  ): { location_id: string; plate_number: string } {
    const hintState = typeof parentContext?.['state'] === 'string' ? parentContext['state'] : null;
    // Hint = selected/parent state: only strip that prefix (keeps vanity bodies like AZ…).
    const { license_plate } = splitLicensePlateForApi(plate, hintState);
    return {
      location_id: locationId,
      plate_number: license_plate || plate,
    };
  }

  private resolveKind(params: OrderRequestWayLookupParams): OrderRequestWayLookupKind | null {
    const plate = String(params.plate ?? '').trim();
    const phone = String(params.phone ?? '').trim();
    const email = String(params.email ?? '').trim();
    const setCount = [plate, phone, email].filter(Boolean).length;
    if (setCount !== 1) {
      return null;
    }
    if (plate) {
      return 'plate';
    }
    if (phone) {
      return 'phone';
    }
    return 'email';
  }

  private resolveLookupValue(
    kind: OrderRequestWayLookupKind,
    params: OrderRequestWayLookupParams
  ): string {
    if (kind === 'plate') {
      return String(params.plate ?? '')
        .trim()
        .toUpperCase();
    }
    if (kind === 'phone') {
      return String(params.phone ?? '').trim();
    }
    return String(params.email ?? '').trim();
  }

  private meetsMinimumInput(kind: OrderRequestWayLookupKind, value: string): boolean {
    if (kind === 'phone') {
      const digits = value.replace(/\D/g, '');
      return digits.length === CUSTOMER_PHONE_MAX_DIGITS;
    }
    if (value.length < 3) {
      return false;
    }
    if (kind === 'email' && !value.includes('@')) {
      return false;
    }
    return true;
  }

  private handleLookupError(
    error: any,
    locationId: string,
    isStale: () => boolean,
    requestKey: string
  ): void {
    if (error instanceof HttpErrorResponse) {
      const code = String(error.error?.code ?? '').trim();
      if (error.status === 422 && code === 'MISSING_WAY_VENDOR_LOCATION_ID') {
        // Record before staleness guard — fact about the location, not this response.
        this.missingWayVendorLocationIds.add(locationId);
        return;
      }
    }

    if (isStale() || this.latestRequestKey !== requestKey) {
      return;
    }

    if (error instanceof HttpErrorResponse) {
      const code = String(error.error?.code ?? '').trim();
      if (error.status === 503 || code === 'WAY_LOOKUP_UNAVAILABLE') {
        return;
      }
    }

    this.logger.trackError('NotificationOrderRequestWayLookup.lookupFromOrderRequest', error);
  }

  clearStoredWayPurchase(): void {
    this.storedPurchaseNotification = null;
    this.storedSearchKey = null;
    this.showWayPurchaseReopenBanner$.next(false);
  }

  reopenStoredWayPurchase(): void {
    if (!this.storedPurchaseNotification) {
      return;
    }
    openWayPurchaseModal(this.wayPurchaseModalState, this.modalStateService, {
      notification: this.storedPurchaseNotification,
      behavior: 'replace',
      shouldStore: false,
      preserveUnderlyingModal: true,
    });
  }

  private isReopenablePurchaseNotification(notification: NotificationData): boolean {
    return (
      notification.type === 'way-single-wash' ||
      notification.type === 'way-active-subscription' ||
      notification.type === 'way-purchase-selection' ||
      notification.type === 'way-purchase-unredeemable' ||
      notification.type === 'way-subscription-already-redeemed-today'
    );
  }

  private rememberPurchaseNotification(notification: NotificationData, searchKey: string): void {
    if (!this.isReopenablePurchaseNotification(notification)) {
      return;
    }
    this.storedPurchaseNotification = notification;
    this.storedSearchKey = searchKey;
    this.showWayPurchaseReopenBanner$.next(true);
  }

  private mergeParentContext(
    notification: NotificationData,
    parentContext?: Record<string, any> | null
  ): NotificationData {
    if (!parentContext || typeof parentContext !== 'object') {
      return notification;
    }
    const existing = (notification.data ?? {}) as Record<string, any>;
    const isOrderRequestLookup = String(existing['source'] ?? '').trim() === 'order_request_lookup';
    const parentForMerge = isOrderRequestLookup
      ? this.stripLprEntryFieldsFromWayLookupParent(parentContext)
      : parentContext;
    return {
      ...notification,
      data: {
        ...parentForMerge,
        ...existing,
        purchase: existing['purchase'] ?? parentForMerge['purchase'],
        purchase_options: existing['purchase_options'] ?? parentForMerge['purchase_options'],
      },
    };
  }

  /** Order-request phone/email/plate lookup must not inherit a stale LPR id from the parent modal. */
  private stripLprEntryFieldsFromWayLookupParent(
    parentContext: Record<string, any>
  ): Record<string, any> {
    const next = { ...parentContext };
    delete next['lpr_entry_id'];
    delete next['lprEntryId'];
    return next;
  }

  private openWayModalIfNew(notification: NotificationData): void {
    const signature = extractWayPurchaseSignature(notification);
    if (signature) {
      const state = this.wayPurchaseModalState.getCurrentState();
      if (state.isOpen && extractWayPurchaseSignature(state.notification) === signature) {
        return;
      }
    }

    openWayPurchaseModal(this.wayPurchaseModalState, this.modalStateService, {
      notification,
      behavior: 'replace',
      shouldStore: false,
      preserveUnderlyingModal: true,
    });
  }
}
